// #4625 — turning "when did this person train" into the shape the review-prompt
// gate reads.
//
// Deliberately split from the database read that feeds it. The client half does
// one view read and hands the workout times in here; every rule about which
// week a workout belongs to lives in this file, where it can be driven directly
// by a test. Calendar arithmetic is the part that goes wrong silently, so it is
// the part that must be testable without a database.
//
// See design-docs/review-prompts.md, "Counting the recent weeks".

import { DateTime } from 'luxon';
import { REVIEW_PROMPT_RULES, WorkoutHistory } from './review-prompt-gate';

/** Days in one of this file's weeks. Named so the arithmetic below reads. */
export const DAYS_PER_TRAINING_WEEK = 7;

/**
 * The earliest moment the read needs to cover, for `weeks` weeks ending at the
 * end of this person's local today.
 *
 * Returned as a moment rather than as a document id so the caller decides how
 * to turn it into one. **No margin is added, and none is needed**: a completed
 * workout's document id is built from its own `time_utc`
 * (`assureIdForEntry` in `data-model/user-log.ts` writes
 * `'log/' + time_utc + '-' + type`), so the id carries the exact instant the
 * workout happened. A range expressed as two instants therefore selects exactly
 * the workouts inside it. The existing range reads in `user-db.service.ts` DO
 * need margins, but for a different reason — they re-read each entry's own
 * recorded wall-clock time in the device's zone, which can move an entry by up
 * to a day. This read deliberately does not do that; `summarizeWorkoutHistory`
 * below says why.
 */
export function reviewPromptHistoryWindowStart(
  now: DateTime,
  weeks: number = REVIEW_PROMPT_RULES.trainingWindowWeeks,
): DateTime {
  return now.endOf('day').minus({ days: weeks * DAYS_PER_TRAINING_WEEK });
}

/**
 * The `startkey` / `endkey` bounds for the `completedWorkouts` view read.
 *
 * **This lives here rather than beside the read for one reason: it is the half
 * that fails silently.** The `completedWorkouts` view emits each entry's
 * document id, and `assureIdForEntry` (`data-model/user-log.ts`) builds that id
 * as `'log/' + time_utc + '-' + type` — so a range over ids IS a range over
 * time, and no separate index or date field is needed. But if either bound
 * drifts from that id shape, or from the window `summarizeWorkoutHistory`
 * buckets against, the read simply comes back short: no error, no report, and
 * the only symptom is that people who qualify are never asked. Keeping the two
 * strings next to the window they must agree with is what lets a test hold them
 * to it.
 *
 * The trailing `￰` on `endkey` is the codebase's existing convention for
 * "everything with this prefix", matching the other view reads in
 * `user-db.service.ts`. It is needed because the id continues past the
 * timestamp with `-Workout`.
 */
export function reviewPromptHistoryKeyRange(
  now: DateTime,
  weeks: number = REVIEW_PROMPT_RULES.trainingWindowWeeks,
): { startkey: string; endkey: string } {
  return {
    startkey: 'log/' + reviewPromptHistoryWindowStart(now, weeks).toUTC().toISO(),
    endkey: 'log/' + now.endOf('day').toUTC().toISO() + '\ufff0',
  };
}

/**
 * Count completed workouts into per-week buckets, most recent week FIRST.
 *
 * **A week here is a 7-day stretch ending at a local midnight, not a calendar
 * Monday-to-Sunday week, and that choice is load-bearing.** Bucket 0 covers the
 * last 7 local days ending with the end of today, bucket 1 the 7 before that,
 * and so on. Real calendar weeks were rejected because the current one is always
 * a partial week: on a Monday it holds at most one day, so somebody who trains
 * four times a week reliably would show an empty bucket 0 and need 5 of the
 * remaining 7 weeks instead of 5 of 8. The same person would then qualify on a
 * Sunday and not on a Monday, which is a behaviour nobody asked for and which
 * would be very hard to explain to whoever noticed it. Fixed 7-day stretches
 * have no partial bucket and no day-of-week dependence.
 *
 * **The buckets are anchored to local midnight, which is what "local" buys
 * here.** Anchoring them to the exact current instant instead would move every
 * boundary through the day, so the same workout could sit in bucket 0 in the
 * morning and bucket 1 in the evening.
 *
 * **Each workout is placed by its UTC instant read in the person's CURRENT
 * zone, not by the zone it was recorded in.** Elsewhere the app uses
 * `getEntryLocalTimeInDesiredTimeZone`, which re-reads an entry's own recorded
 * wall-clock time — but that needs the whole workout document, and this read
 * has to stay cheap and bounded because it happens while somebody is waiting on
 * the app. For anybody who has not changed time zone the two agree exactly. For
 * somebody who has, a workout can move by at most the difference between the
 * two offsets, which only changes an answer when that workout sits within hours
 * of a weekly boundary AND is the only one in its bucket. The result then is one
 * person not being asked to rate the app, which is the safe direction.
 *
 * @param workoutTimes when each completed workout in the window happened.
 *   Invalid entries are skipped rather than poisoning a bucket with NaN.
 * @param now the current time, in the person's own zone.
 * @param weeks how many buckets to produce.
 * @returns the counts and how recently they trained. **This function never
 *   reports "cannot tell"** — that answer belongs to the caller, which is the
 *   only half that can know a read failed. See `WorkoutHistory.weeklyCounts`.
 */
export function summarizeWorkoutHistory(
  workoutTimes: DateTime[],
  now: DateTime,
  weeks: number = REVIEW_PROMPT_RULES.trainingWindowWeeks,
): WorkoutHistory {
  // an unusable clock or a nonsense window size cannot produce buckets that mean
  // anything, so say "cannot tell" rather than returning zeros — zeros would be
  // read as "this person has not trained", which is a different claim. The gate
  // refuses on an unusable clock before it ever looks at these counts, so this
  // is the belt to that braces.
  if (!now || !now.isValid) return {};
  if (!Number.isFinite(weeks) || weeks < 1) return {};

  const weeklyCounts: number[] = new Array(Math.floor(weeks)).fill(0);
  const endOfToday = now.endOf('day');
  const startOfToday = now.startOf('day');
  let daysSinceLastWorkout: number | undefined = undefined;

  for (const workoutTime of workoutTimes || []) {
    if (!workoutTime || !workoutTime.isValid) continue;
    // read the instant in the person's own zone, so the local midnights below
    // are their midnights
    const localTime = workoutTime.setZone(now.zone);

    const daysBack = endOfToday.diff(localTime, 'days').days;
    if (!Number.isFinite(daysBack)) continue;
    // a workout dated in the future is counted as today rather than dropped. It
    // should not happen, but a device clock that is running fast produces one,
    // and dropping it would quietly lose a workout the person really did.
    const bucket = Math.max(0, Math.floor(daysBack / DAYS_PER_TRAINING_WEEK));
    // outside the window the read asked for. Skipped rather than clamped into
    // the oldest bucket, which would inflate that bucket with everything older.
    if (bucket >= weeklyCounts.length) continue;
    weeklyCounts[bucket]++;

    // whole local days, so "trained in the last 10 days" means what somebody
    // saying that sentence means: a workout earlier today is 0 days ago and one
    // yesterday is 1, whatever time of day either happened at. A fractional
    // answer measured from the current instant would make the same workout count
    // as 9 days ago in the morning and 10 in the evening.
    const daysSince = Math.max(0, startOfToday.diff(localTime.startOf('day'), 'days').days);
    if (daysSinceLastWorkout === undefined || daysSince < daysSinceLastWorkout) {
      daysSinceLastWorkout = daysSince;
    }
  }

  return { weeklyCounts, daysSinceLastWorkout };
}
